import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  PackagePlus, Factory, Truck, ListChecks, AlertTriangle, BellRing, ArrowUpRight, Boxes,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { STATUS_BADGE, formatNumber } from "@/lib/constants";
import { KpiTile, DispatchTable, TasksTable, DashHeader, MomStrip } from "@/components/DashboardKit";

export default function ProductionDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notified, setNotified] = useState(() => new Set());
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    api.get("/dashboard/production")
      .then((r) => mounted && setData(r.data))
      .catch(() => toast.error("Failed to load production dashboard"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!data) return;
    const batch = [];
    for (const t of data.tasks || []) if (t.overdue) batch.push({ k: `t-${t.id}`, m: `Overdue task: ${t.task_title}` });
    for (const m of data.materials || []) if (m.qc_status === "Pending") batch.push({ k: `qc-${m.id}`, m: `QC pending: ${m.material_name}` });
    const fresh = batch.filter((b) => !notified.has(b.k)).slice(0, 5);
    if (fresh.length) {
      fresh.forEach((n) => toast.warning(n.m, { icon: "⚠️" }));
      setNotified((prev) => { const n = new Set(prev); fresh.forEach((f) => n.add(f.k)); return n; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading) return <div className="p-6 space-y-4"><Skeleton className="h-10 w-80" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i=><Skeleton key={i} className="h-24" />)}</div><Skeleton className="h-80" /></div>;
  if (!data) return null;
  const s = data.summary || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="production-dashboard-page">
      <DashHeader
        eyebrow="Production / Store Dashboard"
        title={`Hello ${user?.name?.split(" ")[0]} — your shop-floor cockpit`}
        hint="Material inward & QC, production pipeline, dispatch queue and assigned tasks."
        right={<div className="flex gap-2">
          {s.overdue_tasks > 0 && <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50 gap-1"><AlertTriangle className="h-3 w-3" />{s.overdue_tasks} overdue</Badge>}
          {s.qc_pending > 0 && <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50 gap-1"><BellRing className="h-3 w-3" />{s.qc_pending} QC pending</Badge>}
        </div>}
      />

      <MomStrip items={data.mom} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
        <KpiTile label="Material Receipts" value={s.material_receipts} sub={`${s.qc_pending} QC pending`} icon={PackagePlus} accent="blue" testid="kpi-materials" />
        <KpiTile label="In Production" value={s.in_production} sub={`${s.production_records} batches`} icon={Factory} accent="amber" testid="kpi-production" />
        <KpiTile label="Balance Stock" value={formatNumber(s.balance_stock)} sub="units on hand" icon={Boxes} accent="emerald" testid="kpi-stock" />
        <KpiTile label="My Pending Tasks" value={s.pending_tasks} sub={`${s.overdue_tasks} overdue · ${s.due_soon_tasks} due soon`} icon={ListChecks} accent="rose" testid="kpi-tasks" />
      </div>

      <Tabs defaultValue="material" data-testid="production-tabs">
        <TabsList className="h-auto flex-wrap bg-slate-100 p-1">
          <TabsTrigger value="material" data-testid="tab-material"><PackagePlus className="h-3.5 w-3.5 mr-1.5" /> Material Received</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline"><Factory className="h-3.5 w-3.5 mr-1.5" /> Production Pipeline</TabsTrigger>
          <TabsTrigger value="dispatch" data-testid="tab-dispatch"><Truck className="h-3.5 w-3.5 mr-1.5" /> Dispatch Queue</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListChecks className="h-3.5 w-3.5 mr-1.5" /> Pending Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="material"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Material Received & QC</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                {["Date", "Material", "Supplier", "Qty", "PO/Challan", "Accepted", "Rejected", "Store", "QC"].map((h) => (
                  <th key={h} className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                ))}
                <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
              </tr></thead>
              <tbody data-testid="materials-table">
                {data.materials.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-slate-500">No material receipts.</td></tr>}
                {data.materials.map((m) => (
                  <tr key={m.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${m.qc_status === "Pending" ? "bg-amber-50/40" : ""}`} data-testid={`material-row-${m.id}`}>
                    <td className="py-2.5 px-3 text-slate-700">{m.date || "—"}</td>
                    <td className="py-2.5 px-3 font-semibold text-slate-900">{m.material_name || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-700">{m.supplier || "—"}</td>
                    <td className="py-2.5 px-3 font-mono">{formatNumber(m.quantity)} {m.unit || ""}</td>
                    <td className="py-2.5 px-3 font-mono text-slate-600">{m.po_challan_number || "—"}</td>
                    <td className="py-2.5 px-3 font-mono text-emerald-700">{formatNumber(m.accepted_qty)}</td>
                    <td className="py-2.5 px-3 font-mono text-red-600">{formatNumber(m.rejected_qty)}</td>
                    <td className="py-2.5 px-3 text-slate-600">{m.store_location || "—"}</td>
                    <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(m.qc_status || "")}>{m.qc_status || "—"}</Badge></td>
                    <td className="py-2.5 px-3 text-right"><Link to={`/m/material_received/${m.id}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={`material-open-${m.id}`}>Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" /></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card></TabsContent>

        <TabsContent value="pipeline"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Production Pipeline</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                {["Date", "Product", "Model", "Batch", "Order No.", "Produced", "Passed QC", "Rejected", "Balance", "Supervisor"].map((h) => (
                  <th key={h} className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                ))}
                <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
              </tr></thead>
              <tbody data-testid="pipeline-table">
                {data.pipeline.length === 0 && <tr><td colSpan={11} className="text-center py-10 text-slate-500">No production records.</td></tr>}
                {data.pipeline.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`pipeline-row-${p.id}`}>
                    <td className="py-2.5 px-3 text-slate-700">{p.date || "—"}</td>
                    <td className="py-2.5 px-3 font-semibold text-slate-900">{p.product_name || "—"}</td>
                    <td className="py-2.5 px-3 font-mono">{p.model_no || "—"}</td>
                    <td className="py-2.5 px-3 font-mono text-slate-600">{p.batch_no || "—"}</td>
                    <td className="py-2.5 px-3 font-mono text-slate-600">{p.against_order_no || "—"}</td>
                    <td className="py-2.5 px-3 font-mono">{formatNumber(p.qty_produced)}</td>
                    <td className="py-2.5 px-3 font-mono text-emerald-700">{formatNumber(p.qty_passed_qc)}</td>
                    <td className="py-2.5 px-3 font-mono text-red-600">{formatNumber(p.qty_rejected)}</td>
                    <td className="py-2.5 px-3 font-mono font-bold">{formatNumber(p.balance_stock)}</td>
                    <td className="py-2.5 px-3 text-slate-600">{p.production_supervisor || "—"}</td>
                    <td className="py-2.5 px-3 text-right"><Link to={`/m/production/${p.id}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={`pipeline-open-${p.id}`}>Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" /></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card></TabsContent>

        <TabsContent value="dispatch"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Dispatch Queue</div>
          <DispatchTable rows={data.dispatches || []} />
        </CardContent></Card></TabsContent>

        <TabsContent value="tasks"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned Tasks</div>
            <div className="flex gap-2">
              <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50">{s.overdue_tasks} overdue</Badge>
              <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50">{s.due_soon_tasks} due 7d</Badge>
            </div>
          </div>
          <TasksTable rows={data.tasks || []} />
        </CardContent></Card></TabsContent>
      </Tabs>
    </div>
  );
}
